import { createContext, useContext, useState } from "react"
import { cn } from "cn"

import { buildClientAxios } from "@/lib/client-axios"
import { EntitySource, EntitySourceEntry, MessageApi } from "@/lib/message-api"
import IconCircle from "@/components/icons/circle"
import IconSpinner from "@/components/icons/spinner"
import { IconButton } from "./ui/icon-button"
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip"

interface EntitySourceLookup {
  byEntityRef: Map<string, EntitySourceEntry>
  byName: Map<string, EntitySourceEntry>
  callCount: number
}

// Undefined outside the overlay, which is how every entity marker renders
// unchanged unless a message has the overlay switched on.
const EntitySourceContext = createContext<EntitySourceLookup | undefined>(
  undefined
)

export const EntitySourceProvider = EntitySourceContext.Provider

const SOURCE_STYLES = {
  tool: { dot: "bg-green-9", label: "came from a tool call" },
  model: { dot: "bg-amber-9", label: "the model named this one itself" },
  unresolved: { dot: "bg-gray-8", label: "never matched to a place we know" },
} satisfies Record<EntitySourceEntry["source"], { dot: string; label: string }>

const RESOLUTION_LABELS = {
  hint: "the model echoed back the id we handed it",
  facts: "name matched a place a tool offered",
  elastic: "resolved by name search against our index",
  google: "resolved by a Google Places lookup",
} satisfies Record<NonNullable<EntitySourceEntry["resolution"]>, string>

// The org's own tier for the place, on a DMO chat. Distinct in shape as well as
// colour from the source dot beside it, so partner inventory reads at a glance
// without being mistaken for where the place came from.
const PARTNER_STYLES = {
  1: {
    badge: "bg-blue-3 text-blue-11",
    label: "L1 — the org features this place",
  },
  2: {
    badge: "bg-indigo-3 text-indigo-11",
    label: "L2 — the org features this place as premium",
  },
} satisfies Record<
  NonNullable<EntitySourceEntry["partner_level"]>,
  { badge: string; label: string }
>

function tooltipLines(entry: EntitySourceEntry): string[] {
  const lines = [SOURCE_STYLES[entry.source].label]
  entry.calls.forEach((call) => {
    const rank = call.rank ? `, rank ${call.rank}` : ""
    lines.push(`call ${call.index} · ${call.tool_name}${rank}`)
  })
  if (entry.partner_level) lines.push(PARTNER_STYLES[entry.partner_level].label)
  if (entry.resolution) lines.push(RESOLUTION_LABELS[entry.resolution])
  if (entry.detail) lines.push(entry.detail)
  return lines
}

function PartnerBadge({ entry }: { entry: EntitySourceEntry }) {
  if (!entry.partner_level) return null

  return (
    <span
      className={cn(
        "relative top-[-.4em] ml-px inline-flex items-center rounded-sm px-[.15em] align-baseline text-[.55em] leading-[1.4] font-semibold",
        PARTNER_STYLES[entry.partner_level].badge
      )}
    >
      <span>{`L${entry.partner_level}`}</span>
    </span>
  )
}

// Rendered inline after an entity's name. The numeral is the call that offered
// the place: when one tool is called repeatedly to refine a search, a "2" marks
// a place the first call never surfaced.
export function EntitySourceDot({
  entityRef,
  name,
}: {
  entityRef?: string
  name?: string
}) {
  const lookup = useContext(EntitySourceContext)
  if (!lookup) return null

  let entry: EntitySourceEntry | undefined
  if (entityRef) entry = lookup.byEntityRef.get(entityRef)
  else if (name) entry = lookup.byName.get(name)
  if (!entry) return null

  const firstCall = entry.calls[0]
  const index = firstCall && lookup.callCount > 1 ? String(firstCall.index) : ""

  return (
    <>
      <Tooltip>
        <TooltipTrigger
          render={
            <span
              className={cn(
                "relative top-[-.4em] ml-0.5 inline-flex size-[.9em] items-center justify-center rounded-full align-baseline text-[.55em] leading-none font-semibold text-white",
                SOURCE_STYLES[entry.source].dot
              )}
            >
              <span>{index}</span>
            </span>
          }
        />
        <TooltipContent>
          <span className="flex flex-col">
            {tooltipLines(entry).map((line) => (
              <span key={line}>{line}</span>
            ))}
          </span>
        </TooltipContent>
      </Tooltip>
      <PartnerBadge entry={entry} />
    </>
  )
}

function buildLookup(source: EntitySource): EntitySourceLookup {
  const byEntityRef = new Map<string, EntitySourceEntry>()
  const byName = new Map<string, EntitySourceEntry>()
  source.entries.forEach((entry) => {
    if (entry.entity_ref) byEntityRef.set(entry.entity_ref, entry)
    else if (entry.name) byName.set(entry.name, entry)
  })
  return { byEntityRef, byName, callCount: source.calls.length }
}

// Fetches once, then flips the overlay without refetching. The lookup goes to
// the message body via EntitySourceProvider; the button sits beside the bug.
export function useEntitySourceOverlay(messageId: number) {
  const [source, setSource] = useState<EntitySource | null>(null)
  const [showing, setShowing] = useState(false)
  const [loading, setLoading] = useState(false)

  async function toggle() {
    if (showing) {
      setShowing(false)
      return
    }
    if (source) {
      setShowing(true)
      return
    }

    setLoading(true)
    try {
      const debug = await new MessageApi(buildClientAxios()).getDebugInfo(
        messageId
      )
      const fetched = debug.message?.entity_source ?? debug.entity_source
      if (fetched) {
        setSource(fetched)
        setShowing(true)
      }
    } finally {
      setLoading(false)
    }
  }

  return {
    lookup: source && showing ? buildLookup(source) : undefined,
    source,
    showing,
    loading,
    toggle,
  }
}

// The counts read as the answer to "was calling the tool worth it": how much of
// what the bot named the tools actually supplied, and per call, how much of that
// no earlier call had.
function summaryTooltip(source: EntitySource | null): string {
  if (!source) return "Show where each place came from"

  const { grounded, mentioned } = source.summary
  const parts = [`${grounded}/${mentioned} places came from a tool call`]

  const featured = source.entries.filter((entry) => entry.partner_level)
  if (featured.length > 0) {
    const premium = featured.filter((entry) => entry.partner_level === 2).length
    parts.push(
      `${featured.length} featured by the org (${premium} L2, ${featured.length - premium} L1)`
    )
  }

  source.calls.forEach((call) => {
    parts.push(
      `call ${call.index} ${call.tool_name}: offered ${call.offered}, used ${call.used}, new ${call.novel}`
    )
  })
  return parts.join(" · ")
}

export function EntitySourceButton({
  overlay,
}: {
  overlay: ReturnType<typeof useEntitySourceOverlay>
}) {
  return (
    <IconButton
      variant={overlay.showing ? "subtle" : "ghost"}
      icon={overlay.loading ? IconSpinner : IconCircle}
      label="Entity sources"
      tooltip={{ children: <span>{summaryTooltip(overlay.source)}</span> }}
      onClick={() => void overlay.toggle()}
    />
  )
}
